// ─── HUD overlay token set ────────────────────────────────────────────────────
// These tokens drive the glass-morphism floating panel design.
// Every theme must supply one; the `getHud` helper provides a safe fallback.
export interface SessionThemeHud {
  // Floating panel glass effect
  panelGlass: string; // background (rgba)
  panelBorderGlass: string; // border (rgba)
  panelGlow: string; // hover glow box-shadow color
  panelHeaderGradient: string; // title bar gradient (CSS value)

  // Snap-to-edge system
  snapAccent: string; // edge accent bar color
  snapZoneBg: string; // snap-zone indicator fill
  snapZoneBorder: string; // snap-zone dashed border

  // Tether SVG lines (panel → toolbar)
  tetherStroke: string; // bezier path stroke color
  tetherDot: string; // dot at panel anchor point

  // Game board background
  boardGlow1: string; // radial glow 1 (rgba)
  boardGlow2: string; // radial glow 2 (rgba)
  boardGridColor: string; // grid line color (rgba)

  // Bottom toolbar pill
  toolbarBg: string; // pill background
  toolbarBorder: string; // pill border
  toolbarButtonOn: string; // active button background
  toolbarButtonOnGlow: string; // active button glow shadow

  // Top info bar
  topBarFrom: string; // gradient-top opaque color (rgba)
}

// Default HUD tokens — used when a theme omits the `hud` field (dark palette)
const DEFAULT_HUD: SessionThemeHud = {
  panelGlass: "rgba(8,11,20,.88)",
  panelBorderGlass: "rgba(255,255,255,.10)",
  panelGlow: "rgba(124,58,237,.25)",
  panelHeaderGradient: "linear-gradient(90deg,rgba(124,58,237,.12) 0%,transparent 60%)",
  snapAccent: "rgba(124,58,237,.70)",
  snapZoneBg: "rgba(124,58,237,.12)",
  snapZoneBorder: "rgba(124,58,237,.25)",
  tetherStroke: "rgba(124,58,237,.30)",
  tetherDot: "rgba(124,58,237,.60)",
  boardGlow1: "rgba(124,58,237,.07)",
  boardGlow2: "rgba(37,99,235,.06)",
  boardGridColor: "rgba(255,255,255,.018)",
  toolbarBg: "rgba(8,11,20,.92)",
  toolbarBorder: "rgba(255,255,255,.10)",
  toolbarButtonOn: "rgba(124,58,237,.35)",
  toolbarButtonOnGlow: "rgba(124,58,237,.40)",
  topBarFrom: "rgba(8,11,20,.95)",
};

/** Returns the HUD token set for a theme, falling back to the dark default. */
export const getHud = (theme: SessionTheme): SessionThemeHud => theme.hud ?? DEFAULT_HUD;

// ─── Session theme ────────────────────────────────────────────────────────────
// Session theme configurations
export interface SessionTheme {
  id: string;
  name: string;
  description: string;
  /** Optional HUD overlay tokens. When omitted, DEFAULT_HUD is used. */
  hud?: SessionThemeHud;
  colors: {
    // Main colors
    background: string;
    backgroundSecondary: string;
    surface: string;
    surfaceSecondary: string;
    border: string;

    // Text colors
    textPrimary: string;
    textSecondary: string;
    textMuted: string;

    // Accent colors
    accent: string;
    accentHover: string;

    // Status colors
    success: string;
    warning: string;
    error: string;
    info: string;

    // Header
    headerBg: string;
    headerBorder: string;

    // Sidebar
    sidebarBg: string;
    sidebarBorder: string;
    sidebarButtonBg: string;
    sidebarButtonHover: string;
    sidebarButtonActive: string;

    // Panels
    panelBg: string;
    panelBorder: string;
    panelHeader: string;
  };
}

export const sessionThemes: Record<string, SessionTheme> = {
  dark: {
    id: "dark",
    name: "Dark Theme",
    description: "A dark, modern theme perfect for Warhammer 40k and grimdark campaigns",
    hud: {
      panelGlass: "rgba(8,11,20,.88)",
      panelBorderGlass: "rgba(255,255,255,.10)",
      panelGlow: "rgba(124,58,237,.25)",
      panelHeaderGradient: "linear-gradient(90deg,rgba(124,58,237,.12) 0%,transparent 60%)",
      snapAccent: "rgba(124,58,237,.70)",
      snapZoneBg: "rgba(124,58,237,.12)",
      snapZoneBorder: "rgba(124,58,237,.25)",
      tetherStroke: "rgba(124,58,237,.30)",
      tetherDot: "rgba(124,58,237,.60)",
      boardGlow1: "rgba(124,58,237,.07)",
      boardGlow2: "rgba(37,99,235,.06)",
      boardGridColor: "rgba(255,255,255,.018)",
      toolbarBg: "rgba(8,11,20,.92)",
      toolbarBorder: "rgba(255,255,255,.10)",
      toolbarButtonOn: "rgba(124,58,237,.35)",
      toolbarButtonOnGlow: "rgba(124,58,237,.40)",
      topBarFrom: "rgba(8,11,20,.95)",
    },
    colors: {
      background: "#111827",
      backgroundSecondary: "#1f2937",
      surface: "#374151",
      surfaceSecondary: "#4b5563",
      border: "#6b7280",

      textPrimary: "#f9fafb",
      textSecondary: "#e5e7eb",
      textMuted: "#9ca3af",

      accent: "#8b5cf6",
      accentHover: "#7c3aed",

      success: "#10b981",
      warning: "#f59e0b",
      error: "#ef4444",
      info: "#3b82f6",

      headerBg: "#1f2937",
      headerBorder: "#374151",

      sidebarBg: "#1f2937",
      sidebarBorder: "#374151",
      sidebarButtonBg: "#374151",
      sidebarButtonHover: "#4b5563",
      sidebarButtonActive: "#8b5cf6",

      panelBg: "#1f2937",
      panelBorder: "#4b5563",
      panelHeader: "#374151",
    },
  },

  steampunk: {
    id: "steampunk",
    name: "Steampunk Theme",
    description: "Victorian industrial aesthetic with brass and copper tones",
    hud: {
      panelGlass: "rgba(30,16,5,.90)",
      panelBorderGlass: "rgba(205,133,63,.22)",
      panelGlow: "rgba(205,133,63,.22)",
      panelHeaderGradient: "linear-gradient(90deg,rgba(205,133,63,.15) 0%,transparent 60%)",
      snapAccent: "rgba(205,133,63,.75)",
      snapZoneBg: "rgba(205,133,63,.10)",
      snapZoneBorder: "rgba(205,133,63,.28)",
      tetherStroke: "rgba(205,133,63,.38)",
      tetherDot: "rgba(205,133,63,.70)",
      boardGlow1: "rgba(205,133,63,.08)",
      boardGlow2: "rgba(139,90,43,.06)",
      boardGridColor: "rgba(205,133,63,.028)",
      toolbarBg: "rgba(22,11,2,.93)",
      toolbarBorder: "rgba(205,133,63,.28)",
      toolbarButtonOn: "rgba(205,133,63,.32)",
      toolbarButtonOnGlow: "rgba(205,133,63,.40)",
      topBarFrom: "rgba(22,11,2,.95)",
    },
    colors: {
      background: "#2d1b0e",
      backgroundSecondary: "#3d2515",
      surface: "#4d3220",
      surfaceSecondary: "#5d3e28",
      border: "#8b6f47",

      textPrimary: "#f4e4d4",
      textSecondary: "#d4c4b4",
      textMuted: "#a4947c",

      accent: "#cd853f",
      accentHover: "#b8860b",

      success: "#6b8e23",
      warning: "#daa520",
      error: "#b22222",
      info: "#4682b4",

      headerBg: "#3d2515",
      headerBorder: "#5d3e28",

      sidebarBg: "#3d2515",
      sidebarBorder: "#5d3e28",
      sidebarButtonBg: "#4d3220",
      sidebarButtonHover: "#6d4e30",
      sidebarButtonActive: "#cd853f",

      panelBg: "#3d2515",
      panelBorder: "#6d4e30",
      panelHeader: "#5d3e28",
    },
  },

  light: {
    id: "light",
    name: "Light Fun Theme",
    description: "Bright and cheerful theme perfect for lighthearted D&D campaigns",
    hud: {
      panelGlass: "rgba(255,255,255,.93)",
      panelBorderGlass: "rgba(0,0,0,.12)",
      panelGlow: "rgba(245,158,11,.18)",
      panelHeaderGradient: "linear-gradient(90deg,rgba(245,158,11,.10) 0%,transparent 60%)",
      snapAccent: "rgba(245,158,11,.80)",
      snapZoneBg: "rgba(245,158,11,.08)",
      snapZoneBorder: "rgba(245,158,11,.22)",
      tetherStroke: "rgba(245,158,11,.38)",
      tetherDot: "rgba(245,158,11,.70)",
      boardGlow1: "rgba(245,158,11,.05)",
      boardGlow2: "rgba(59,130,246,.04)",
      boardGridColor: "rgba(0,0,0,.028)",
      toolbarBg: "rgba(255,255,255,.96)",
      toolbarBorder: "rgba(0,0,0,.12)",
      toolbarButtonOn: "rgba(245,158,11,.22)",
      toolbarButtonOnGlow: "rgba(245,158,11,.28)",
      topBarFrom: "rgba(255,255,255,.96)",
    },
    colors: {
      background: "#f9fafb",
      backgroundSecondary: "#f3f4f6",
      surface: "#e5e7eb",
      surfaceSecondary: "#d1d5db",
      border: "#9ca3af",

      textPrimary: "#111827",
      textSecondary: "#374151",
      textMuted: "#6b7280",

      accent: "#f59e0b",
      accentHover: "#d97706",

      success: "#10b981",
      warning: "#f59e0b",
      error: "#ef4444",
      info: "#3b82f6",

      headerBg: "#ffffff",
      headerBorder: "#e5e7eb",

      sidebarBg: "#ffffff",
      sidebarBorder: "#e5e7eb",
      sidebarButtonBg: "#f3f4f6",
      sidebarButtonHover: "#e5e7eb",
      sidebarButtonActive: "#f59e0b",

      panelBg: "#ffffff",
      panelBorder: "#d1d5db",
      panelHeader: "#f3f4f6",
    },
  },

  industrial: {
    id: "industrial",
    name: "Industrial Theme",
    description: "Cold metal and concrete aesthetic for cyberpunk or post-apocalyptic settings",
    hud: {
      panelGlass: "rgba(18,18,20,.90)",
      panelBorderGlass: "rgba(6,182,212,.16)",
      panelGlow: "rgba(6,182,212,.20)",
      panelHeaderGradient: "linear-gradient(90deg,rgba(6,182,212,.12) 0%,transparent 60%)",
      snapAccent: "rgba(6,182,212,.72)",
      snapZoneBg: "rgba(6,182,212,.10)",
      snapZoneBorder: "rgba(6,182,212,.22)",
      tetherStroke: "rgba(6,182,212,.32)",
      tetherDot: "rgba(6,182,212,.68)",
      boardGlow1: "rgba(6,182,212,.06)",
      boardGlow2: "rgba(20,184,166,.05)",
      boardGridColor: "rgba(255,255,255,.015)",
      toolbarBg: "rgba(12,12,14,.93)",
      toolbarBorder: "rgba(6,182,212,.22)",
      toolbarButtonOn: "rgba(6,182,212,.28)",
      toolbarButtonOnGlow: "rgba(6,182,212,.36)",
      topBarFrom: "rgba(12,12,14,.95)",
    },
    colors: {
      background: "#18181b",
      backgroundSecondary: "#27272a",
      surface: "#3f3f46",
      surfaceSecondary: "#52525b",
      border: "#71717a",

      textPrimary: "#fafafa",
      textSecondary: "#e4e4e7",
      textMuted: "#a1a1aa",

      accent: "#06b6d4",
      accentHover: "#0891b2",

      success: "#14b8a6",
      warning: "#f97316",
      error: "#dc2626",
      info: "#06b6d4",

      headerBg: "#27272a",
      headerBorder: "#3f3f46",

      sidebarBg: "#27272a",
      sidebarBorder: "#3f3f46",
      sidebarButtonBg: "#3f3f46",
      sidebarButtonHover: "#52525b",
      sidebarButtonActive: "#06b6d4",

      panelBg: "#27272a",
      panelBorder: "#52525b",
      panelHeader: "#3f3f46",
    },
  },

  parchment: {
    id: "parchment",
    name: "Parchment Theme",
    description: "Ancient scroll aesthetic perfect for fantasy and historical campaigns",
    hud: {
      panelGlass: "rgba(245,230,210,.95)",
      panelBorderGlass: "rgba(141,110,99,.30)",
      panelGlow: "rgba(141,110,99,.20)",
      panelHeaderGradient: "linear-gradient(90deg,rgba(141,110,99,.12) 0%,transparent 60%)",
      snapAccent: "rgba(141,110,99,.78)",
      snapZoneBg: "rgba(141,110,99,.10)",
      snapZoneBorder: "rgba(141,110,99,.25)",
      tetherStroke: "rgba(141,110,99,.38)",
      tetherDot: "rgba(141,110,99,.68)",
      boardGlow1: "rgba(141,110,99,.07)",
      boardGlow2: "rgba(109,76,65,.05)",
      boardGridColor: "rgba(0,0,0,.025)",
      toolbarBg: "rgba(237,220,196,.96)",
      toolbarBorder: "rgba(141,110,99,.35)",
      toolbarButtonOn: "rgba(141,110,99,.25)",
      toolbarButtonOnGlow: "rgba(141,110,99,.32)",
      topBarFrom: "rgba(237,220,196,.96)",
    },
    colors: {
      background: "#f5e6d3",
      backgroundSecondary: "#ede0c8",
      surface: "#e5d4b8",
      surfaceSecondary: "#ddc9a3",
      border: "#c4b5a0",

      textPrimary: "#3e2723",
      textSecondary: "#5d4037",
      textMuted: "#795548",

      accent: "#8d6e63",
      accentHover: "#6d4c41",

      success: "#558b2f",
      warning: "#ef6c00",
      error: "#c62828",
      info: "#1976d2",

      headerBg: "#ede0c8",
      headerBorder: "#ddc9a3",

      sidebarBg: "#ede0c8",
      sidebarBorder: "#ddc9a3",
      sidebarButtonBg: "#e5d4b8",
      sidebarButtonHover: "#ddc9a3",
      sidebarButtonActive: "#8d6e63",

      panelBg: "#ede0c8",
      panelBorder: "#c4b5a0",
      panelHeader: "#e5d4b8",
    },
  },
};

// Helper function to get theme by ID
export const getTheme = (themeId: string): SessionTheme => {
  return sessionThemes[themeId] || sessionThemes.dark;
};

// Get all available themes as an array
export const getAvailableThemes = (): SessionTheme[] => {
  return Object.values(sessionThemes);
};
